/**
 * Payment Method Selector Component
 * 
 * Displays saved payment methods and allows the user to select one
 * or add a new payment method.
 * 
 * @module butterUI/components
 */

import { useId } from 'react'
import { BankIcon, CardBrandIcon, CardPaymentIcon, CheckmarkIcon } from '#icons'
import { describePaymentMethod, isBankPaymentMethod } from '../paymentMethodDisplay'

/**
 * Minimal payment method shape needed for display.
 * Compatible with both the app-local `PaymentMethod` and the shared
 * `PaymentMethodDisplay` from `@lgc/database`, so consumers don't need
 * to map between them.
 */
export interface PaymentMethodItem {
  id: string | number;
  methodType?: 'card' | 'bank' | string | null;
  cardBrand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  isDefault?: boolean;
  // Bank (ACH) — present only on bank methods
  bankName?: string | null;
  bankLast4?: string | null;
  bankAccountType?: string | null;
}

/**
 * Method types no checkout surface can accept yet.
 *
 * A bank (ACH) charge returns `processing` from the provider, and the charge path
 * rejects that with `PENDING_SETTLEMENT_UNSUPPORTED` because no settlement webhook
 * exists to later mark the invoice paid. So a member selecting a saved bank account
 * dead-ends at the confirm button.
 *
 * Module-level so it is one stable reference shared by every call site (no new array
 * identity per render), and so **Phase 3 deletes one constant** instead of hunting
 * literals across two apps. Pair with the server-side guard in
 * `getBestAvailablePaymentMethod`, which blocks the same rows from AUTO-selection.
 */
export const UNSELECTABLE_METHOD_TYPES: readonly string[] = ['bank'];

export interface PaymentMethodSelectorProps {
  /** Array of saved payment methods */
  paymentMethods: PaymentMethodItem[];
  
  /** Currently selected payment method ID */
  selectedPaymentMethodId: string | null;
  
  /** Callback when a payment method is selected */
  onSelect: (id: string) => void;
  
  /** Callback when "Add new payment method" is clicked */
  onAddNew: () => void;
  
  /** Whether the component is in a loading/processing state */
  isProcessing?: boolean;

  /**
   * Method types that cannot be selected on this surface. Matching rows still
   * RENDER — greyed out with {@link unselectableReason} — rather than disappearing,
   * because a member whose saved bank account silently vanished from checkout will
   * file a ticket, and support has no way to explain it.
   *
   * Used today to block `'bank'` at checkout: a bank charge returns `processing`,
   * which the charge path rejects with `PENDING_SETTLEMENT_UNSUPPORTED` because no
   * settlement webhook exists yet. Remove the prop from call sites when that lands
   * (Phase 3) — the component keeps working with it absent.
   */
  unselectableMethodTypes?: readonly string[];

  /** Shown on a row disabled by {@link unselectableMethodTypes}. */
  unselectableReason?: string;

  /**
   * Row treatment. `default` is the original checkout list (unchanged).
   * `tile` is the checkout design: dark brand tile, "•••• 1234 / Exp. MM/YY",
   * neutral card with a dark check when selected, and an add-method tile.
   * @default 'default'
   */
  variant?: 'default' | 'tile';

  /** List heading. `null` hides it (the caller already titles the section). @default 'Saved Payment Methods' */
  heading?: string | null;
}

/**
 * Payment Method Selector
 * 
 * Renders a list of saved payment methods with radio-button style selection.
 * 
 * @example
 * ```tsx
 * <PaymentMethodSelector
 *   paymentMethods={paymentMethods}
 *   selectedPaymentMethodId={selectedId}
 *   onSelect={setSelectedId}
 *   onAddNew={() => setShowNewPaymentForm(true)}
 * />
 * ```
 */
export function PaymentMethodSelector({
  paymentMethods,
  selectedPaymentMethodId,
  onSelect,
  onAddNew,
  isProcessing = false,
  unselectableMethodTypes,
  unselectableReason = 'Not available for payment yet',
  variant = 'default',
  heading = 'Saved Payment Methods',
}: PaymentMethodSelectorProps) {
  // Called unconditionally regardless of variant, so hook order never changes.
  const radioName = useId();
  // Absent prop ⇒ nothing is blocked, so every existing consumer is unchanged.
  const isUnselectable = (pm: PaymentMethodItem) =>
    !!unselectableMethodTypes?.includes(pm.methodType ?? 'card');

  if (variant === 'tile') {
    return (
      <fieldset>
        {heading === null ? (
          <legend className="sr-only">Saved payment methods</legend>
        ) : (
          <legend className="mb-6 text-sm font-medium text-fg-secondary md:mb-7">{heading}</legend>
        )}

        <div className="space-y-4 md:space-y-5">
        {paymentMethods.map((pm) => {
          const id = pm.id.toString();
          const selected = selectedPaymentMethodId === id;
          const unselectable = isUnselectable(pm);
          const desc = describePaymentMethod(pm);
          const isBank = isBankPaymentMethod(pm);
          const reasonId = `pm-unselectable-${pm.id}`;
          const expiry =
            pm.expMonth && pm.expYear
              ? `Exp. ${String(pm.expMonth).padStart(2, '0')}/${String(pm.expYear).slice(-2)}`
              : null;
          const secondaryLine = isBank
            ? `${desc.name}${desc.sublabel ? ` · ${desc.sublabel}` : ''}`
            : expiry;

          return (
            <label
              key={id}
              className="block cursor-pointer"
              data-testid="payment-method-option"
              data-method-id={id}
            >
              <input
                type="radio"
                name={radioName}
                className="peer sr-only"
                checked={selected}
                disabled={isProcessing || unselectable}
                aria-describedby={unselectable ? reasonId : undefined}
                onChange={() => onSelect(id)}
              />
              <div className="flex items-center gap-4 rounded-18 border border-butter-gray-200 bg-bg-tertiary p-4 md:p-6 peer-focus-visible:border-theme-600 peer-disabled:opacity-50">
                <div className="flex h-11 w-16 shrink-0 items-center justify-center rounded-md bg-fg-primary">
                  {isBank ? (
                    <BankIcon className="h-6 w-9 text-white" />
                  ) : (
                    <CardBrandIcon brand={pm.cardBrand ?? ''} className="h-6 w-9" />
                  )}
                </div>
                <div>
                  <p className="text-base font-semibold text-fg-secondary">•••• {desc.last4 || '••••'}</p>
                  {secondaryLine && <p className="text-sm font-medium text-fg-tertiary">{secondaryLine}</p>}
                  {unselectable && (
                    <p id={reasonId} className="text-sm italic text-fg-tertiary">
                      {unselectableReason}
                    </p>
                  )}
                </div>
                {selected && !unselectable && (
                  <div className="ml-auto flex size-6 items-center justify-center rounded-full bg-fg-primary text-white">
                    <CheckmarkIcon className="size-3" ariaHidden />
                  </div>
                )}
              </div>
            </label>
          );
        })}

        <button
          type="button"
          onClick={onAddNew}
          disabled={isProcessing}
          data-testid="payment-method-add"
          className="flex w-full items-center gap-4 rounded-18 border-1.25 border-butter-gray-100 bg-white p-4 text-interactive:border-theme-300 disabled:opacity-50 md:justify-center md:p-6"
        >
          <div className="flex h-11 w-16 items-center justify-center rounded-md border border-butter-gray-100 bg-bg-tertiary">
            <CardPaymentIcon className="size-6 text-fg-secondary" ariaHidden />
          </div>
          <span className="text-base font-semibold text-fg-secondary">+ Add new payment method</span>
        </button>
        </div>
      </fieldset>
    );
  }

  // Get card brand icon
  const getCardIcon = (brand?: string) => {
    const brandLower = brand?.toLowerCase();
    
    // Card brands with specific icons
    if (brandLower === 'visa') {
      return (
        <svg className="w-10 h-6" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#1A1F71"/>
          <path d="M20.5 11.5L18 20.5H15.5L13 11.5H15.5L16.75 17.25L18 11.5H20.5ZM25 11.5L22.5 20.5H20.5L23 11.5H25ZM30 11.5C30.5 11.5 31 11.75 31.25 12.25L33 20.5H30.5L30.25 19H28L27.75 20.5H25.5L27.25 12.25C27.5 11.75 28 11.5 28.5 11.5H30ZM29 14.5L28.5 17H29.75L29.25 14.5H29ZM36 11.5C36.75 11.5 37.25 12 37.25 12.75V20.5H35V14.5H33.5V20.5H31.5V11.5H36Z" fill="white"/>
        </svg>
      );
    }
    
    if (brandLower === 'mastercard') {
      return (
        <svg className="w-10 h-6" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#EB001B"/>
          <circle cx="20" cy="16" r="8" fill="#FF5F00"/>
          <circle cx="28" cy="16" r="8" fill="#F79E1B"/>
        </svg>
      );
    }
    
    if (brandLower === 'amex' || brandLower === 'american express') {
      return (
        <svg className="w-10 h-6" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#006FCF"/>
          <path d="M15 11H18L19.5 14L21 11H24V21H21.5V14.5L19.5 18H17.5L15.5 14.5V21H13V11H15ZM27 11H33V13H27.5V15H32.5V17H27.5V19H33V21H27C26.5 21 26 20.5 26 20V12C26 11.5 26.5 11 27 11Z" fill="white"/>
        </svg>
      );
    }
    
    if (brandLower === 'discover') {
      return (
        <svg className="w-10 h-6" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#FF6000"/>
          <circle cx="36" cy="16" r="8" fill="#FFD700"/>
        </svg>
      );
    }
    
    // Default credit card icon
    return (
      <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    );
  };

  return (
    <div className="space-y-3">
      {heading !== null && (
        <h3 className="text-sm font-medium text-fg-secondary mb-2">
          {heading}
        </h3>
      )}

      {paymentMethods.map((pm) => {
        const unselectable = isUnselectable(pm);
        return (
        <button
          key={pm.id}
          type="button"
          onClick={() => onSelect(pm.id.toString())}
          disabled={isProcessing || unselectable}
          // Announce WHY it's disabled — `disabled` alone is silent to a screen
          // reader, and the visual reason below isn't linked to the control.
          aria-describedby={unselectable ? `pm-unselectable-${pm.id}` : undefined}
          className={`
            w-full p-4 border-2 rounded-lg cursor-pointer transition-all text-left
            ${selectedPaymentMethodId === pm.id.toString()
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-300 bg-white'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isBankPaymentMethod(pm) ? <BankIcon className="w-10 h-6" /> : getCardIcon(pm.cardBrand)}
              {(() => {
                const desc = describePaymentMethod(pm);
                return (
                  <div>
                    <p className="font-medium text-fg-primary">
                      •••• {desc.last4 || '****'}
                    </p>
                    <p className="text-sm text-fg-tertiary">
                      {desc.name}
                      {desc.sublabel && <span className="ml-2">{desc.sublabel}</span>}
                    </p>
                    {unselectable && (
                      <p id={`pm-unselectable-${pm.id}`} className="text-sm text-fg-tertiary italic">
                        {unselectableReason}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {!unselectable && selectedPaymentMethodId === pm.id.toString() && (
              <div className="flex items-center text-blue-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="ml-1 font-medium text-sm">Selected</span>
              </div>
            )}
          </div>
        </button>
        );
      })}

      <button
        type="button"
        onClick={onAddNew}
        disabled={isProcessing}
        className="
          w-full px-4 py-3 text-blue-600 hover:text-blue-700 font-medium text-sm 
          border-2 border-blue-600 hover:border-blue-700 rounded-lg 
          transition-colors disabled:opacity-50 disabled:cursor-not-allowed
          bg-white hover:bg-blue-50
        "
      >
        + Add new payment method
      </button>
    </div>
  );
}
